import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import type {
  FormField as ImportFormField,
  SourceFormSource,
  SourceOptions,
  SourceResource,
} from "@prequel/import-react";
import {
  useCreateSource,
  useImportContext,
  useSourceFormFields,
  useTestExistingSource,
  useTestNewSource,
  useUpdateSource,
} from "@prequel/import-react";
import {
  Alert,
  AlertKind,
  Button,
  ButtonStyle,
  Dropdown,
  FormField,
  SearchableDropdown,
  type SearchableDropdownListItem,
  Spinner,
} from "@prequel/shared-components";

import type { TestConnectionResult } from "@/api/client";
import { getErrorMessage } from "@/api/helpers";
import { useAllProviders } from "@/api/import/providers";
import ApiFormField from "@/components/ApiFormField";
import { Divider, FormFooter, FormSection, ReadOnlyFormValue } from "@/components/import/FormLayout";
import TestConnectionCard from "@/components/TestConnectionCard";
import { sourceFormValues } from "@/pages/import/SourcesPage/sourceFormValues";
import { useToast } from "@/providers/ToastProvider";

type SourceVendor = NonNullable<SourceOptions["vendor"]>;

const EMPTY_CONNECTION_TEST: TestConnectionResult = { status: undefined };
// Matches the label the source form endpoint gives the vendor field, for the states that render without it.
const SOURCE_TYPE_LABEL = "Source type";
const SOURCE_NAME_DESCRIPTION =
  "Descriptive and unique name for this source. This will only be visible internally and is only used as a reference.\n(e.g., Acme Corp Snowflake)";

type SourceFormProps = {
  sourceId?: string;
  source?: SourceResource;
  cancelPath?: string;
  onSaved?: () => void;
};

const SourceForm = ({ sourceId, source: existingSource, cancelPath = "/import/sources", onSaved }: SourceFormProps) => {
  const navigate = useNavigate();
  const isEditing = !!sourceId;
  const formRef = useRef<HTMLFormElement>(null);
  const { addToast } = useToast();
  const providersQuery = useAllProviders();
  const { sources: sourcesApi } = useImportContext();
  const createSource = useCreateSource();
  const updateSource = useUpdateSource();
  const testSource = useTestNewSource();
  const testExisting = useTestExistingSource();
  const [providerID, setProviderID] = useState(existingSource?.provider_id ?? "");
  const [sourceVendor, setSourceVendor] = useState<SourceVendor | "">(
    (existingSource?.vendor as SourceVendor | undefined) ?? "",
  );
  const [sourceVendorField, setSourceVendorField] = useState<ImportFormField>();
  const [sourceVendorError, setSourceVendorError] = useState<unknown>();
  const [isLoadingSourceVendors, setIsLoadingSourceVendors] = useState(true);
  const [sourceName, setSourceName] = useState(existingSource?.name ?? "");
  const [connectionTest, setConnectionTest] = useState<TestConnectionResult>(EMPTY_CONNECTION_TEST);
  const [testedSourceFingerprint, setTestedSourceFingerprint] = useState<string>();

  const hasSelectedVendor = sourceVendor.length > 0;
  const canConfigureConnection = providerID.length > 0 && hasSelectedVendor;
  const initialSource = useMemo<SourceFormSource | undefined>(
    () => (existingSource ? sourceFormValues(existingSource) : undefined),
    [existingSource],
  );
  const form = useSourceFormFields({
    providerId: providerID,
    vendor: sourceVendor,
    enabled: canConfigureConnection,
    mode: isEditing ? "edit" : "create",
    initialSource,
  });
  const sourceFingerprint = useMemo(() => JSON.stringify(form.getSource({ name: sourceName })), [form, sourceName]);
  const currentConnectionTest = testedSourceFingerprint === sourceFingerprint ? connectionTest : EMPTY_CONNECTION_TEST;
  const providerItems = useMemo<SearchableDropdownListItem<string>[]>(
    () =>
      providersQuery.items.map((provider) => ({
        key: provider.id,
        text: `${provider.name} (${provider.id})`,
        searchValue: `${provider.name} ${provider.id}`,
      })),
    [providersQuery.items],
  );
  const selectedProvider = providerItems.find((item) => item.key === providerID);
  const providerLabel = useMemo(() => {
    const provider = providersQuery.items.find((candidate) => candidate.id === providerID);
    return provider ? `${provider.name} (${provider.id})` : providerID;
  }, [providersQuery.items, providerID]);
  const vendorLabel =
    sourceVendorField?.enum?.find((option) => option.key === sourceVendor)?.display ?? String(sourceVendor);
  const isMutating =
    createSource.isCreating || updateSource.isUpdating || testSource.isTesting || testExisting.isTesting;
  const isPreparingForm = form.isLoading || form.isRefetching;
  const canSave = currentConnectionTest.status === "success";

  useEffect(() => {
    let isActive = true;
    setIsLoadingSourceVendors(true);
    setSourceVendorError(undefined);

    sourcesApi
      .createForm({})
      .then((response) => {
        if (!isActive) {
          return;
        }

        setSourceVendorField(
          response.fields.find((field) => field.path === "source.vendor" || field.name === "vendor"),
        );
      })
      .catch((error) => {
        if (isActive) {
          setSourceVendorError(error);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingSourceVendors(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [sourcesApi]);

  const onProviderSelected = (item: SearchableDropdownListItem<string>) => {
    setProviderID(item.key);
    setConnectionTest(EMPTY_CONNECTION_TEST);
    setTestedSourceFingerprint(undefined);
  };

  const onSourceVendorSelected = (_field: ImportFormField, value: unknown) => {
    setSourceVendor(value ? (String(value) as SourceVendor) : "");
    setConnectionTest(EMPTY_CONNECTION_TEST);
    setTestedSourceFingerprint(undefined);
  };

  const testConnection = useCallback(async () => {
    if (!formRef.current?.reportValidity()) {
      return;
    }

    const testedFingerprint = sourceFingerprint;
    setTestedSourceFingerprint(testedFingerprint);
    setConnectionTest({ status: "processing" });

    try {
      const result = sourceId
        ? await testExisting.testExistingSource(sourceId, form.getUpdate({ name: sourceName }))
        : await testSource.testNewSource(form.getSource({ name: sourceName }));
      setConnectionTest({ status: result.status, message: result.message });
    } catch (error) {
      setConnectionTest({ status: "error", message: getErrorMessage(error) });
    }
  }, [form, sourceFingerprint, sourceId, sourceName, testExisting, testSource]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canConfigureConnection || !canSave) {
      return;
    }

    try {
      if (sourceId) {
        await updateSource.updateSource(sourceId, form.getUpdate({ name: sourceName }));
        onSaved?.();
        addToast({ kind: "SUCCESS", message: "Source updated successfully!" });
      } else {
        const created = await createSource.createSource(form.getSource({ name: sourceName }));
        addToast({ kind: "SUCCESS", message: "Source created successfully!" });
        navigate(`/import/sources/${created.id}`);
        return;
      }
      navigate(`/import/sources/${sourceId}`);
    } catch {
      addToast({ kind: "ERROR", message: isEditing ? "Source update failed." : "Source creation failed." });
    }
  };

  if (providersQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <form className="space-y-8 pb-16" onSubmit={onSubmit} ref={formRef}>
      {!!providersQuery.error && <Alert kind={AlertKind.ERROR}>Failed to load providers.</Alert>}
      {!!sourceVendorError && <Alert kind={AlertKind.ERROR}>Failed to load source types.</Alert>}
      {!!form.error && <Alert kind={AlertKind.ERROR}>Failed to generate source form.</Alert>}

      <FormSection title="Select a provider">
        {isEditing ? (
          <ReadOnlyFormValue label="Provider ID" value={providerLabel} />
        ) : (
          <SearchableDropdown
            label="Provider ID"
            items={providerItems}
            selectedItem={selectedProvider}
            setSelectedItem={onProviderSelected}
            emptyText="No providers are available."
            disabled={isMutating}
            subtext="Provider name (id)"
          />
        )}
      </FormSection>

      <Divider />

      <FormSection title="Name and configure the source">
        <div className="space-y-6">
          <LocalSourceNameField value={sourceName} setValue={setSourceName} disabled={isMutating} />
          {isEditing ? (
            <ReadOnlyFormValue label={SOURCE_TYPE_LABEL} value={vendorLabel} />
          ) : sourceVendorField ? (
            <ApiFormField
              field={sourceVendorField}
              values={{ vendor: sourceVendor as SourceVendor }}
              setFieldValue={onSourceVendorSelected}
              disabled={isMutating || isLoadingSourceVendors}
            />
          ) : (
            <Dropdown
              label={SOURCE_TYPE_LABEL}
              items={[]}
              selectedItem={undefined}
              setSelectedItem={() => undefined}
              disabled
            />
          )}
        </div>
      </FormSection>

      <Divider />

      {canConfigureConnection && !form.isLoading && (
        <>
          <div className="space-y-6">
            {form.fields.map((field) => (
              <ApiFormField
                key={field.path}
                field={field}
                values={form.draft}
                setFieldValue={form.setSourceField}
                disabled={isMutating}
              />
            ))}
          </div>

          <TestConnectionCard
            test={currentConnectionTest}
            request={{
              status: form.isLoading || form.isRefetching || createSource.isCreating ? "processing" : undefined,
            }}
            connectionType="source"
            usesPreparedConnection
            onTestConnection={testConnection}
          />
        </>
      )}

      {!canConfigureConnection && (
        <Alert kind={AlertKind.INFO}>Select a Provider and Source type to configure connection details.</Alert>
      )}

      <FormFooter justify="end">
        <Button type={ButtonStyle.TERTIARY} text="Cancel" onClick={() => navigate(cancelPath)} disabled={isMutating} />
        <Button
          type={canSave ? ButtonStyle.PRIMARY : ButtonStyle.TERTIARY}
          disabled={!canConfigureConnection || isMutating || isPreparingForm || !canSave}
          submit
          text={
            createSource.isCreating || updateSource.isUpdating ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-white" />
                {isEditing ? "Updating Source..." : "Saving Source..."}
              </div>
            ) : isEditing ? (
              "Update Source"
            ) : (
              "Save Source"
            )
          }
        />
      </FormFooter>
    </form>
  );
};

const LocalSourceNameField = ({
  value,
  setValue,
  disabled,
}: {
  value: string;
  setValue: (value: string) => void;
  disabled: boolean;
}) => (
  <FormField
    id="source-name-placeholder"
    label="Source name"
    type="text"
    value={value}
    onChangeHandler={setValue}
    required
    disabled={disabled}
    subtext={SOURCE_NAME_DESCRIPTION}
  />
);

export default SourceForm;
